/**
 * Förgenererade, cachebara bildfiler från public/media.
 *
 * De här filerna finns färdiga på disk i stället för att kodas om av Vite vid
 * första besöket. Det tar bort flera sekunders väntan i preview och ger samma
 * små AVIF/WebP-filer i utveckling och produktion.
 */

export type ResponsiveImage = {
  avif: string;
  webp: string;
  src: string;
  mobile?: { avif: string; webp: string; src: string };
};

const srcSet = (name: string, widths: number[], format: "avif" | "webp") =>
  widths.map((width) => `/media/${name}-${width}.${format} ${width}w`).join(", ");

const versionedSrcSet = (name: string, widths: number[], format: "avif" | "webp", version: string) =>
  widths.map((width) => `/media/${name}-${width}.${format}?v=${version} ${width}w`).join(", ");

const standard = (name: string): ResponsiveImage => ({
  avif: srcSet(name, [320, 480, 768, 1024], "avif"),
  webp: srcSet(name, [320, 480, 768, 1024], "webp"),
  src: `/media/${name}.jpg`,
});

const versionedStandard = (name: string, version: string): ResponsiveImage => ({
  avif: versionedSrcSet(name, [320, 480, 768, 1024], "avif", version),
  webp: versionedSrcSet(name, [320, 480, 768, 1024], "webp", version),
  src: `/media/${name}.jpg?v=${version}`,
});

export const heroCoastAvifSrc = "/media/hero-1440.avif";
export const heroCoastImage: ResponsiveImage = {
  avif: srcSet("hero", [480, 768, 1024, 1440, 1920], "avif"),
  webp: srcSet("hero", [480, 768, 1024, 1440, 1920], "webp"),
  src: "/media/hero.jpg",
  mobile: {
    avif: [480, 640, 768].map((width) => `/media/hero/mobile-${width}.avif ${width}w`).join(", "),
    webp: [480, 640, 768].map((width) => `/media/hero/mobile-${width}.webp ${width}w`).join(", "),
    src: "/media/hero/mobile.jpg",
  },
};

export const classroomImage = standard("classroom");
export const campusImage = standard("campus");
export const preschoolPlayImage = standard("preschool");
export const schoolyardImage = standard("schoolyard");
export const footballImage = standard("football");
export const padelImage = standard("padel");
export const campImage = versionedStandard("campFootball", "20260925");
export const sportsFootballImage = versionedStandard("sportsFootball", "20260906");
export const sportsPadelImage = versionedStandard("sportsPadel", "20260906b");
export const sportsTennisImage = versionedStandard("sportsTennis", "20260906b");
export const profileFootballImage = standard("profileFootball");
export const profilePadelImage = standard("profilePadel");
export const profileSchoolSportImage = standard("profileSchoolSport");
export const profileJuntaImage = standard("profileJunta");
export const aboutCourtyardImage = standard("aboutCourtyard");
export const aboutCommunityImage = standard("aboutCommunity");
export const blogSkolstartImage = standard("blogSkolstart");
export const blogSommarImage = standard("blogSommar");
export const homePreschoolImage = standard("homePreschool");
export const homePreschoolClassImage = standard("homePreschoolClass");
export const bandDeskImage = standard("bandDesk");
export const homePrimaryImage = standard("homeGrundskola");
export const homeSecondaryImage = standard("homeSecondary");
export const homeGymnasiumImage = standard("homeGymnasium");
export const coachTeensImage: ResponsiveImage = {
  avif: srcSet("coachTeens", [320, 480, 768], "avif"),
  webp: srcSet("coachTeens", [320, 480, 768], "webp"),
  src: "/media/coachTeens.jpg",
};

export const mffPassImage: ResponsiveImage = {
  avif: srcSet("mffPass", [480, 768, 1024, 1440], "avif"),
  webp: srcSet("mffPass", [480, 768, 1024, 1440], "webp"),
  src: "/media/mffPass-1024.webp",
};
export const malmoSkolanImage: ResponsiveImage = {
  avif: srcSet("malmoSkolan", [480, 768, 1024, 1440], "avif"),
  webp: srcSet("malmoSkolan", [480, 768, 1024, 1440], "webp"),
  src: "/media/malmoSkolan-1024.webp",
};
export const mffLogoSrcset = "/media/mffLogo-120.webp 120w, /media/mffLogo-196.webp 196w";
export const mffLogoSrc = "/media/mffLogo-196.webp";

export const knowledgeImages: Record<string, ResponsiveImage> = {
  flyttchecklistan: standard("kbFlytt"),
  "myndigheter-och-papper": standard("kbPapper"),
  "vardagen-i-spanien": standard("kbVardag"),
  "trygghet-och-viktiga-nummer": standard("kbTrygghet"),
};